import { useCallback, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import api from '../services/api';

/**
 * Shared search-mutation core for Gallery/Places/Entities (COL-138).
 *
 * Search is a `useMutation`, not a `useQuery`, on all three pages: it's a
 * one-off fetch triggered by user action, not page data to revisit, so it
 * doesn't need a cache key (COL-88's query cache is for *page* data —
 * items, enrichments — not this). All three pages hand-rolled an identical
 * mutation + `searching` flag + `isSubmit -> includeAnswer` mapping before
 * this hook existed — the mapping is COL-95: `includeAnswer` is only true
 * on an explicit Enter/submit, never a debounced keystroke pause, because
 * every debounce pause would otherwise re-fire the answer-generating LLM
 * call. Owning that mapping in one place means a future change to it
 * applies everywhere at once, instead of three copies needing to be kept in
 * sync by hand.
 *
 * What the three pages actually differed on (COL-138), and how each is
 * threaded through here rather than absorbed into one shared behavior:
 *   - `scope`: Gallery searches `'items'`, Places/Entities search
 *     `'entities'`.
 *   - `categoryFilter`: Gallery-only. Passing it as `undefined` (Places/
 *     Entities never pass it at all) produces byte-identical request
 *     bodies either way — `JSON.stringify` drops `undefined` values, and
 *     `api.search()` already spreads `category_filter: options.categoryFilter`
 *     unconditionally — so there is nothing to special-case here.
 *   - `transformResults`: Places filters the raw response down to
 *     `entity_type === 'place'` before it becomes `searchResults`; Gallery
 *     and Entities pass the response through unchanged (the default,
 *     omitting this option).
 *   - `clearStaleAnswerOnSearch`: Gallery only. Nulls out a stale AI answer
 *     the instant a new search starts, while leaving the previous items in
 *     place — the precondition for the stale-while-refetch dim treatment
 *     Gallery's render layer applies via `searching`. Places/Entities never
 *     did this and instead render a full skeleton on every search, old or
 *     new — a pre-existing gap between the three pages, not something this
 *     refactor closes; see the COL-138 PR notes before "fixing" it here.
 *
 * What stays page-specific, deliberately NOT absorbed into this hook:
 *   - URL `?q=` sync (Gallery only, COL-87) and the local `searchQuery`
 *     state Places/Entities keep instead — both wrap the `search()`
 *     returned below with their own extra bookkeeping.
 *   - Deep-link restore-on-mount (Gallery only, an effect that fires once
 *     `?q=` is present on mount) — it calls the raw `searchMutation.mutate`
 *     this hook also returns, unchanged from before extraction, because it
 *     deliberately skips the trim-check `search()` applies (the URL's `q`
 *     is trusted as-is on mount, not re-validated).
 *   - Stale-while-refetch *rendering* — pages read `searching` to decide
 *     between a full skeleton and a dimmed grid; this hook only owns
 *     pre-clearing the stale answer, not how a page paints while
 *     `searching` is true.
 *
 * @param {Object} options
 * @param {'items'|'entities'} options.scope
 * @param {string} [options.categoryFilter] - Gallery only.
 * @param {(raw: object) => object} [options.transformResults] - applied to
 *   a successful response before it becomes `searchResults`.
 * @param {boolean} [options.clearStaleAnswerOnSearch] - default false.
 * @param {() => void} [options.onError] - every current caller passes the
 *   same `() => showToast('Search failed', 'error')`; kept as a parameter
 *   rather than hardcoded since toasting is page UI, not this hook's job.
 */
export function useSearch({
  scope,
  categoryFilter,
  transformResults,
  clearStaleAnswerOnSearch = false,
  onError,
} = {}) {
  const [searchResults, setSearchResults] = useState(null);

  const searchMutation = useMutation({
    mutationFn: ({ query, includeAnswer }) => api.search(query, {
      scope,
      categoryFilter,
      includeAnswer,
    }),
    onSuccess: (results) => {
      setSearchResults(transformResults ? transformResults(results) : results);
    },
    onError: () => { onError?.(); },
  });

  const searching = searchMutation.isPending;

  const search = useCallback((query, { isSubmit = false } = {}) => {
    const trimmed = query.trim();
    if (!trimmed) {
      setSearchResults(null);
      return;
    }
    if (clearStaleAnswerOnSearch) {
      setSearchResults(prev => (prev ? { ...prev, answer: null } : null));
    }
    searchMutation.mutate({ query, includeAnswer: isSubmit });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchMutation, clearStaleAnswerOnSearch]);

  return { searchResults, setSearchResults, searching, search, searchMutation };
}
